import { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import { ALL_WORKING_UNITS_OPTION, useWorkingUnits } from '../../hooks';
import WorkingUnitAutocomplete from '../Inputs/Autocomplete/WorkingUnitAutocomplete';
import { WorkingUnit } from '../../types/WorkingUnit.interface';
import type { Dayjs } from '@utils/dayjs';

interface BiddingCalculationModalProps {
    open: boolean;
    onClose: () => void;
    onCalculate: (params: { date: Dayjs; workingUnits?: number[] }) => void;
    initialDate: Dayjs;
    initialWorkingUnitId?: number;
    isLoading?: boolean;
}

const BiddingCalculationModal: React.FC<BiddingCalculationModalProps> = ({
    open,
    onClose,
    onCalculate,
    initialDate,
    isLoading = false,
}) => {
    const { t } = useTranslation();
    const [selectedWorkingUnits, setSelectedWorkingUnits] = useState<WorkingUnit[]>([ALL_WORKING_UNITS_OPTION]);

    const [workingUnits] = useWorkingUnits({
        hasMatchingOfferedCluster: true,
        prependAllOption: true,
    });

    const handleCalculate = () => {
        if (selectedWorkingUnits && selectedWorkingUnits.length > 0) {
            onCalculate({
                date: initialDate,
                workingUnits:
                    selectedWorkingUnits.length === 1 && selectedWorkingUnits[0].id === ALL_WORKING_UNITS_OPTION.id
                        ? undefined
                        : selectedWorkingUnits.map((wu) => wu.id),
            });
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown={isLoading}>
            <DialogTitle>
                <Typography variant="h6">
                    {t('bidding.calculationTitle', {
                        month: initialDate.format(DATE_FORMATS.MONTH_NAME_YEAR),
                    })}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <WorkingUnitAutocomplete<true>
                                workingUnitOptions={workingUnits}
                                selectedWorkingUnits={selectedWorkingUnits}
                                onChange={setSelectedWorkingUnits}
                                isMultiSelect={true}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isLoading}>
                    {t('action.close')}
                </Button>
                <Button
                    onClick={handleCalculate}
                    variant="contained"
                    disabled={selectedWorkingUnits.length === 0 || isLoading}
                >
                    <span>{isLoading ? t('bidding.calculating') : t('action.calculate')}</span>
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BiddingCalculationModal;
